import { useState, useRef, FC, useEffect, useCallback } from 'react';
import { useDebounceCallback, useResizeObserver } from 'usehooks-ts';
import { cn } from '@fe-learner-common';
import { MoreCategoriesMenu } from '@routes/catalog/components/Categories/MoreCategoriesMenu';
import { CategoryLinkItem } from '@routes/catalog/components/Categories/CategoryLinkItem';
import { MappedCategoryData } from '@hooks/useFilterCategoryData';
import { TCategory } from '@api/io/categories.io';

type CategoriesProps = {
  categoryData?: MappedCategoryData | null;
};
type Size = {
  width: number | undefined;
  height: number | undefined;
};

const sortCategories = (a: TCategory, b: TCategory) => {
  const nameA = a.name || '';
  const nameB = b.name || '';
  return nameA.localeCompare(nameB) || (!nameA ? 1 : -1);
};

export const Categories: FC<CategoriesProps> = ({ categoryData }) => {
  const { categories: unsortedCategories, selectedCategory } =
    categoryData || {};
  const categories = unsortedCategories?.sort(sortCategories);

  const containerRef = useRef<HTMLDivElement>(null);
  const menuBtnRef = useRef<HTMLButtonElement>(null);
  const pseudoContainerRef = useRef<HTMLDivElement>(null);
  const [visibleItems, setVisibleItems] = useState<Set<number>>(new Set());

  const onResize = useCallback(
    (size: Size) => {
      if (
        !containerRef.current ||
        !pseudoContainerRef.current ||
        !size.width ||
        !categories
      )
        return;

      const children = Array.from(
        pseudoContainerRef.current.children,
      ) as HTMLElement[];

      // hide all children that doesn't fully fit in the container. use getBoundingClientRect to get the right position
      const menuBtnWidth =
        menuBtnRef.current?.getBoundingClientRect().width || 0;
      const visibleItems = new Set<number>();
      let totalWidth = 0;

      for (let i = 0; i < children.length; i++) {
        const child = children[i];
        const { width } = child.getBoundingClientRect();
        const isVisible = totalWidth + width + menuBtnWidth <= size.width - 16;
        if (isVisible) {
          visibleItems.add(Number(child.dataset.category));
        }
        totalWidth += width + 16;
      }

      setVisibleItems(visibleItems);
    },
    [categories],
  );
  const debounced = useDebounceCallback(onResize, 50);

  useEffect(() => {
    if (categories) {
      onResize({ width: containerRef.current?.clientWidth, height: 0 });
    }
  }, [categories, onResize]);

  useResizeObserver({
    ref: containerRef,
    box: 'border-box',
    onResize: debounced,
  });

  const hiddenCategories = categories?.filter(
    (cat) => !visibleItems.has(cat.id),
  );

  return (
    <div
      ref={containerRef}
      className="relative flex w-full gap-2 overflow-hidden p-3 "
    >
      <div ref={pseudoContainerRef} className="invisible absolute flex gap-2">
        {categories?.map((item, index) => (
          <div
            key={index}
            data-category={item.id}
            className={cn(
              'l-btn l-btn-md l-btn-secondary flex-shrink-0 text-nowrap ',
            )}
          >
            {item.name}
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        {categories
          ?.filter((item) => visibleItems.has(item.id))
          .map((item, index) => (
            <CategoryLinkItem category={item} key={index} />
          ))}
      </div>

      {categories && hiddenCategories?.length ? (
        <MoreCategoriesMenu ref={menuBtnRef} categories={hiddenCategories} />
      ) : null}

      {selectedCategory && !selectedCategory.children.length ? (
        <CategoryLinkItem category={selectedCategory} />
      ) : null}
    </div>
  );
};
